"use client";

/**
 * Google Analytics 4 mount point.
 *
 * Loads gtag.js and reports a page_view on initial load and on every
 * client-side route change. Reports into the same data stream as
 * www.gobo.show, so a landing page → signup journey is one GA4 session.
 * Production only (see isGaEnabled). Mounted exactly once from <ClientLayout>.
 *
 * ⚠️ Two deliberate guards against double-counting page_view, which is the bug
 * MetaPixel.tsx has. Do not "simplify" either one away:
 *
 *  1. **The dependency is `searchParams?.toString()`, a string — never the
 *     `searchParams` object.** useSearchParams() returns a
 *     ReadonlyURLSearchParams, and React compares deps with Object.is, so a new
 *     instance on any re-render re-runs the effect. This component lives inside
 *     ClientLayout, which re-renders on auth state, user data and the burger
 *     menu, so an object dep reports navigations that never happened.
 *
 *  2. **gtag is configured with `send_page_view: false`** (in configureGa) so
 *     the config call doesn't emit its own page_view alongside ours.
 *
 * A URL de-duplication ref backs both up: React StrictMode double-invokes
 * effects in development, and it costs nothing to make a repeat fire
 * impossible rather than merely unlikely.
 */

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import Script from "next/script";
import {
  configureGa,
  gaMeasurementId,
  isGaEnabled,
  trackPageView,
} from "../lib/ga";
import { logError } from "../lib/bugsnag";

export default function GoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // A string, deliberately. See guard 1 in the header.
  const query = searchParams?.toString() ?? "";

  const configuredRef = useRef(false);
  const lastUrlRef = useRef<string | null>(null);

  const sink = (error: unknown, meta?: Record<string, unknown>) =>
    logError(error, { context: "GoogleAnalytics", ...meta });

  // Configure the stream once per page load, before any page_view is queued.
  // gtag.js drains the queue in order when it loads, so ordering holds even
  // though this runs before the script has executed.
  useEffect(() => {
    if (configuredRef.current) return;
    configuredRef.current = true;
    configureGa(sink);
  }, []);

  // One page_view per actual URL change — initial load included, since the
  // config call no longer sends one.
  useEffect(() => {
    const url = query ? `${pathname}?${query}` : pathname ?? "/";
    if (lastUrlRef.current === url) return;
    lastUrlRef.current = url;
    trackPageView(url, typeof document !== "undefined" ? document.title : undefined, sink);
  }, [pathname, query]);

  const id = gaMeasurementId();
  if (!id || !isGaEnabled()) return null;

  return (
    <Script
      id="ga4-gtag"
      strategy="afterInteractive"
      src={`https://www.googletagmanager.com/gtag/js?id=${id}`}
      // ⚠️ Deliberately NOT reported to Bugsnag, which is the one exception to
      // the never-swallow rule in CLAUDE.md — and it is an exception because
      // nothing failed. An ad blocker refusing googletagmanager.com is the
      // expected outcome for a sizeable share of visitors, not a defect: it is
      // the user's choice, we cannot act on it, and there is no fix to ship.
      // It was reported for a fortnight and produced 26 reports from 13 users
      // saying only that. Whether GA is actually collecting is a question for
      // GA4's own traffic numbers, where an absence is visible; an error
      // tracker cannot tell "blocked by choice" from "broken".
      onError={() => {
        if (typeof console !== "undefined") {
          console.warn("gtag.js did not load — usually an ad blocker.");
        }
      }}
    />
  );
}
